import { useReducer } from 'react';

import type { ValidationMessageLevel } from '@/services/bulk-donor-scheduling';

import { BulkDonorScheduleInput } from '../utils/schema';

export type Messages = Record<
  string,
  {
    level: ValidationMessageLevel;
    message: string;
    suggestions?: string[];
  }
>;

export type ViewType = 'donor' | 'donor-schedule';

export type SheetState = (
  | {
      type: 'idle';
      readonly: false;
      loading: false;
      errors: Record<string, string> | undefined;
      messages: Messages | undefined;
    }
  | {
      type: 'client_validating';
      readonly: true;
      loading: true;
      errors: undefined;
      messages: undefined;
    }
  | {
      type: 'server_validating';
      readonly: true;
      loading: true;
      errors: undefined;
      messages: undefined;
    }
  | {
      type: 'server_validated';
      readonly: false;
      loading: false;
      errors: undefined;
      messages: Messages | undefined;
    }
  | {
      type: 'uploading';
      readonly: true;
      loading: true;
      errors: undefined;
      messages: Messages | undefined;
    }
  | {
      type: 'success';
      readonly: true;
      loading: false;
      errors: undefined;
      messages: Messages | undefined;
    }
) & {
  rows: BulkDonorScheduleInput[];
  viewType: ViewType;
};

const initialState: SheetState = {
  type: 'idle',
  readonly: false,
  loading: false,
  rows: Array.from({ length: 31 }) as any[],
  errors: undefined,
  messages: undefined,
  viewType: 'donor-schedule',
};

type SheetActions =
  | { type: 'reset' }
  | { type: 'idle' }
  | { type: 'add_rows'; count: number }
  | { type: 'set_row'; rowIndex: number; data: BulkDonorScheduleInput }
  | { type: 'client_validating' }
  | {
      type: 'client_validated';
      errors: Record<string, string> | undefined;
    }
  | {
      type: 'server_validated';
      errors: Record<string, string> | undefined;
      messages: Messages | undefined;
    }
  | { type: 'uploading' }
  | { type: 'server_error' }
  | { type: 'success' }
  | { type: 'switch_view'; viewType: ViewType }
  | { type: 'upload_from_excel'; data: BulkDonorScheduleInput[] };

const sheetStateReducer: React.Reducer<SheetState, SheetActions> = (
  state,
  action,
) => {
  switch (action.type) {
    case 'reset':
      return initialState;
    case 'idle':
      return {
        ...state,
        type: 'idle',
        readonly: false,
        loading: false,
      } satisfies SheetState;
    case 'add_rows':
      return {
        type: 'idle',
        readonly: false,
        loading: false,
        rows: state.rows.concat(Array.from({ length: action.count })),
        errors: undefined,
        messages: undefined,
        viewType: state.viewType,
      } satisfies SheetState;
    case 'set_row':
      if (action.rowIndex < 0 || action.rowIndex >= state.rows.length) {
        return state;
      }
      return {
        type: 'idle',
        readonly: false,
        loading: false,
        rows: state.rows.map((row, i) =>
          i === action.rowIndex ? action.data : row,
        ),
        errors: state.errors,
        messages: state.messages,
        viewType: state.viewType,
      } satisfies SheetState;
    case 'client_validating':
      return {
        type: 'client_validating',
        readonly: true,
        loading: true,
        rows: state.rows,
        errors: undefined,
        messages: undefined,
        viewType: state.viewType,
      } satisfies SheetState;
    case 'client_validated':
      return action.errors
        ? ({
            type: 'idle',
            readonly: false,
            loading: false,
            rows: state.rows,
            errors: action.errors,
            messages: undefined,
            viewType: state.viewType,
          } satisfies SheetState)
        : ({
            type: 'server_validating',
            readonly: true,
            loading: true,
            rows: state.rows,
            errors: undefined,
            messages: undefined,
            viewType: state.viewType,
          } satisfies SheetState);
    case 'server_validated':
      return action.errors
        ? ({
            type: 'idle',
            readonly: false,
            loading: false,
            rows: state.rows,
            errors: action.errors,
            messages: action.messages,
            viewType: state.viewType,
          } satisfies SheetState)
        : ({
            type: 'server_validated',
            readonly: false,
            loading: false,
            rows: state.rows,
            errors: undefined,
            messages: action.messages,
            viewType: state.viewType,
          } satisfies SheetState);
    case 'uploading':
      return {
        type: 'uploading',
        readonly: true,
        loading: true,
        rows: state.rows,
        errors: undefined,
        messages: state.messages,
        viewType: state.viewType,
      } satisfies SheetState;
    case 'server_error':
      return {
        type: 'idle',
        readonly: false,
        loading: false,
        rows: state.rows,
        errors: state.errors,
        messages: state.messages,
        viewType: state.viewType,
      } satisfies SheetState;
    case 'success':
      return {
        type: 'success',
        readonly: true,
        loading: false,
        rows: state.rows,
        errors: undefined,
        messages: state.messages,
        viewType: state.viewType,
      } satisfies SheetState;
    case 'switch_view':
      return {
        ...state,
        viewType: action.viewType,
      } satisfies SheetState;
    case 'upload_from_excel':
      return {
        type: 'idle',
        readonly: false,
        loading: false,
        rows: Array.from({ length: Math.max(31, action.data.length) }).map(
          (emptyRow, i) =>
            action.data[i] ? action.data[i] : (emptyRow as any),
        ),
        errors: undefined,
        messages: undefined,
        viewType: state.viewType,
      } satisfies SheetState;
    default:
      return state;
  }
};

export function useBulkDonorSchedulingStore() {
  return useReducer(sheetStateReducer, initialState);
}
